// Compatibility shim for Rebuy Smart Cart mode.
//
// When `settings.cart_drawer_provider == 'rebuy'`, the Dawn `<cart-drawer>` element is no
// longer rendered (theme.liquid) and `assets/cart-drawer.js` is not loaded (header.liquid).
// But five callers still do `document.querySelector('cart-drawer').renderContents(...)` after
// an add — frequently-bought.js (postBundleToCart), free-gift-modal.js, progress-bar.js,
// free-gift-validator.js, rebuy-bundle-validator.js. With the real element gone those would
// throw on a null lookup.
//
// This shim defines a hidden `<cart-drawer>` element that resolves the lookup and maps the
// Dawn drawer API onto Rebuy Smart Cart: renderContents() opens Smart Cart (and asks Rebuy to
// refresh so a freshly-added line shows). It degrades to a no-op if `window.Rebuy` is not ready,
// so a failed/late Rebuy load never breaks the add flow.

import { syncCartUI, updateCartBubble } from './rebuy-cart-sync.js';

// Force Rebuy to re-read the Shopify cart. An item added via the Shopify AJAX API (product-form,
// FBT, etc.) is NOT added through Rebuy.Cart, so Smart Cart shows a stale cart until it re-syncs.
// The real method is `Rebuy.Cart.fetchCart()` (there is no `.refresh()` — calling it silently
// no-ops and the drawer opens empty, then the item pops in ~400ms later).
const refreshRebuyCart = (rebuy) => {
    try {
        if (rebuy.Cart && typeof rebuy.Cart.fetchCart === 'function') {
            rebuy.Cart.fetchCart();
        }
    } catch (error) {
        console.error('Rebuy cart fetch failed:', error);
    }
};

// Wait until the Smart Cart flyout DOM is actually populated before we show it, so it opens with the
// item visible — not empty-then-pop. Rebuy's `.rebuy-cart` carries a `no-items` class while empty;
// it clears (~500ms after an add) once Rebuy re-renders the flyout, and crucially it clears WHILE
// the drawer is still hidden (because we set skip_open). Waiting on the count alone is not enough:
// Rebuy's cart cache reports the new count before the flyout DOM re-renders, so the drawer would
// still flash empty. The `no-items` class is the true "rendered with items" signal.
// `waitForPopulated=false` (plain cart-icon open) resolves immediately. Always resolves by timeout.
const waitForDrawerPopulated = (waitForPopulated, timeout = 2500) => {
    if (!waitForPopulated) return Promise.resolve();
    return new Promise((resolve) => {
        const start = Date.now();
        const tick = () => {
            const root = document.querySelector('.rebuy-cart');
            if (root && !root.classList.contains('no-items')) {
                resolve();
            } else if (Date.now() - start > timeout) {
                resolve();
            } else {
                setTimeout(tick, 80);
            }
        };
        tick();
    });
};

// Rebuy lazy-loads the Smart Cart module: `Rebuy.SmartCart` is undefined until either Rebuy's own
// bound cart trigger is clicked or `Rebuy.importSmartCartModule()` is called. Since we open the
// drawer programmatically (bypassing Rebuy's trigger binding), we trigger the import ourselves and
// wait for `SmartCart.show` to exist. Resolves true once ready, false after a timeout.
const ensureSmartCartReady = (rebuy) => {
    if (rebuy.SmartCart && typeof rebuy.SmartCart.show === 'function') {
        return Promise.resolve(true);
    }
    if (typeof rebuy.importSmartCartModule === 'function') {
        try {
            rebuy.importSmartCartModule();
        } catch (error) {
            console.error('Rebuy importSmartCartModule failed:', error);
        }
    }
    return new Promise((resolve) => {
        const start = Date.now();
        const timer = setInterval(() => {
            if (rebuy.SmartCart && typeof rebuy.SmartCart.show === 'function') {
                clearInterval(timer);
                resolve(true);
            } else if (Date.now() - start > 5000) {
                clearInterval(timer);
                resolve(false);
            }
        }, 150);
    });
};

// Open the Smart Cart, importing the lazy module first if needed. `afterAdd` (an add just happened)
// makes us refresh Rebuy's cart and wait until the flyout DOM is populated BEFORE showing, so the
// drawer opens already showing the item instead of flashing empty. Returns true on success so the
// caller can fall back (e.g. navigate to /cart) when Rebuy never becomes ready.
const openSmartCart = async (afterAdd) => {
    const rebuy = window.Rebuy;
    if (!rebuy) {
        console.warn('Rebuy not ready; cannot open Smart Cart.');
        return false;
    }
    const ready = await ensureSmartCartReady(rebuy);
    if (!ready) {
        console.warn('Rebuy Smart Cart module unavailable; skipping drawer open.');
        return false;
    }
    refreshRebuyCart(rebuy);
    await waitForDrawerPopulated(afterAdd);
    try {
        rebuy.SmartCart.show();
        return true;
    } catch (error) {
        console.error('Rebuy Smart Cart show failed:', error);
        return false;
    }
};

class CartDrawerShim extends HTMLElement {
    // Dawn API surface used by the five callers. `parsedState` is the /cart/add response, which
    // carries the new `item_count`. We don't render the drawer markup — Smart Cart owns that — but
    // we update the header bubble instantly from that count, then let Rebuy's cart.change event
    // (which also fires for native adds) reconcile everything else via rebuy-cart-sync.
    renderContents(parsedState) {
        if (parsedState && typeof parsedState.item_count === 'number') {
            updateCartBubble(parsedState.item_count);
        }
        syncCartUI();
        // Open AFTER the flyout is populated so the drawer never flashes empty (afterAdd = true).
        openSmartCart(true);
    }

    // Callers build a `sections=` POST param from this list; keep the same ids so the request
    // shape is unchanged even though the returned section HTML is ignored in Smart Cart mode.
    getSectionsToRender() {
        return [{ id: 'cart-drawer' }, { id: 'cart-icon-bubble' }];
    }

    // product-form.js / quick-add.js call `this.cart.setActiveElement(document.activeElement)`
    // BEFORE posting to /cart/add (mirrors Dawn's cart-drawer.js). Without it the add throws and
    // nothing is added to the cart. Store it for parity; we don't use it (Rebuy owns focus).
    setActiveElement(element) {
        this.activeElement = element;
    }
}

// Bind the header cart icon to Smart Cart. The icon is a plain `<a href="/cart">`; in Dawn mode
// cart-drawer.js intercepted the click, but that script is not loaded in rebuy mode. In rebuy mode
// the icon must ONLY ever open the Smart Cart drawer — it must NEVER navigate to the /cart page
// (per product decision). So we always preventDefault, and never fall back to the href.
const CART_ICON_LOADING_CLASS = 'cart-icon--smart-cart-loading';

// Gate the header cart icon on Smart Cart readiness. Rebuy lazy-loads the Smart Cart module, so for
// a beat after page load (and after a bfcache restore on browser Back) `SmartCart.show` doesn't
// exist yet. While not-ready we mark the icon `aria-disabled` (CSS shows not-allowed) and the click
// handler bails — the icon stays not-clickable until Rebuy binds. We never re-enable on timeout,
// because re-enabling without a working drawer (and with /cart navigation removed) would just be a
// dead click; staying disabled is the honest signal.
const setCartIconReady = (ready) => {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.classList.toggle(CART_ICON_LOADING_CLASS, !ready);
    cartLink.setAttribute('aria-disabled', ready ? 'false' : 'true');
};

const bindCartIcon = () => {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.smartCartBound) return;
    cartLink.dataset.smartCartBound = 'true';
    cartLink.addEventListener('click', async (event) => {
        // The icon never navigates to /cart in rebuy mode — it only opens the drawer.
        event.preventDefault();
        // Not yet bound to Smart Cart (initial load or bfcache restore) — ignore the click.
        if (cartLink.getAttribute('aria-disabled') === 'true') return;
        await openSmartCart();
    });
};

// Suppress Rebuy's auto-open-on-add. By default Rebuy opens Smart Cart the instant it detects a
// cart add — BEFORE its cart copy syncs (~400ms) — so the drawer flashes EMPTY then the item pops
// in. `Rebuy.SmartCart.skip_open = true` disables that (manual show() still works); we then open the
// drawer ourselves, already populated, from renderContents/the cart icon. We must set the flag
// before the first add, but SmartCart is lazy-loaded, so we preload the module and set it once ready.
let readinessTimer = null;
const waitForSmartCartReady = () => {
    // Re-runnable: a prior poll (e.g. from a bfcache restore) is cleared first.
    clearInterval(readinessTimer);
    const start = Date.now();
    readinessTimer = setInterval(() => {
        const rebuy = window.Rebuy;
        if (rebuy) {
            if (rebuy.SmartCart && typeof rebuy.SmartCart.show === 'function') {
                rebuy.SmartCart.skip_open = true; // suppress Rebuy's auto-open-on-add (see below).
                setCartIconReady(true); // module bound — clicks can now open the drawer.
                clearInterval(readinessTimer);
                return;
            }
            // Preload the lazy module so the flag lands before any add would auto-open it.
            if (typeof rebuy.importSmartCartModule === 'function') {
                try {
                    rebuy.importSmartCartModule();
                } catch (error) {
                    console.error('Rebuy importSmartCartModule failed:', error);
                }
            }
        }
        // Give it longer (15s) for slow loads; on timeout leave the icon disabled rather than make
        // it a dead click. A working store always binds well within this.
        if (Date.now() - start > 15000) clearInterval(readinessTimer);
    }, 150);
};

// Suppress Rebuy's auto-open-on-add. By default Rebuy opens Smart Cart the instant it detects a
// cart add — BEFORE its cart copy syncs (~400ms) — so the drawer flashes EMPTY then the item pops
// in. `Rebuy.SmartCart.skip_open = true` disables that (manual show() still works); we then open the
// drawer ourselves, already populated. The flag is set by waitForSmartCartReady once the module is
// ready (same poll that re-enables the cart icon).
const disableRebuyAutoOpen = () => waitForSmartCartReady();

// Browser Back restores the page from bfcache with the icon's stale `aria-disabled` state, but
// Rebuy's Smart Cart module isn't re-bound yet — a click would hit a not-ready drawer. Re-gate:
// disable the icon and re-run the readiness poll (which re-enables it once Rebuy is ready again).
const regateOnBfcacheRestore = () => {
    window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        setCartIconReady(false);
        waitForSmartCartReady();
    });
};

// Wire the shim. Safe to call once on load in rebuy mode only (see 10x_global.js gate).
// The `<cart-drawer>` element is server-rendered in theme.liquid (so product-form/quick-add capture
// it synchronously at construct time); here we just upgrade it via customElements.define.
export const initCartDrawerShim = () => {
    if (!customElements.get('cart-drawer')) {
        customElements.define('cart-drawer', CartDrawerShim);
    }
    setCartIconReady(false); // disable the icon until Smart Cart binds (re-enabled by the poll).
    bindCartIcon();
    disableRebuyAutoOpen();
    regateOnBfcacheRestore();
};

export default initCartDrawerShim;
